import { Bell, AlertTriangle, TrendingUp, Package, Radio, FileWarning } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useMergedAlerts, type MergedAlert } from '@/hooks/use-merged-alerts';
import { useUpdateAlertState } from '@/hooks/use-alert-lifecycle';
import { useAgency } from '@/contexts/AgencyContext';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { AlertActions } from './AlertActions';

const ICONS: Record<string, any> = {
  creator_dropoff: TrendingUp,
  low_posting: TrendingUp,
  sample_followup: Package,
  live_inactivity: Radio,
  import_failed: FileWarning,
  top_spike: TrendingUp,
};

const SEV_CLS: Record<string, string> = {
  critical: 'text-destructive bg-destructive/10',
  warn: 'text-warning bg-warning/10',
  success: 'text-success bg-success/10',
  info: 'text-primary bg-primary/10',
};

export function AlertsBell() {
  const { active, unreadCount } = useMergedAlerts();
  const navigate = useNavigate();
  const { agencyId } = useAgency();
  const update = useUpdateAlertState();

  const markAllRead = () => {
    active.filter(a => a.isUnread).forEach(a =>
      update.mutate({
        alertKey: a.id,
        alertKind: a.kind,
        brandId: a.brandId ?? null,
        agencyId: agencyId ?? null,
        status: 'read',
      }),
    );
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative h-8 w-8 text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors">
          <Bell className="h-4 w-4" />
          {unreadCount > 0 && (
            <span className="absolute top-1 right-1 min-w-[14px] h-[14px] px-1 rounded-full bg-destructive text-[9px] font-bold text-destructive-foreground flex items-center justify-center">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[400px] p-0 surface-raised">
        <div className="px-3 py-2.5 border-b border-border flex items-center justify-between">
          <span className="text-sm font-semibold">Operational alerts</span>
          <div className="flex items-center gap-3">
            {active.some(a => a.isUnread) && (
              <button className="text-[11px] text-muted-foreground hover:text-foreground" onClick={markAllRead}>
                Mark all read
              </button>
            )}
            <button className="text-[11px] text-primary hover:underline" onClick={() => navigate('/alerts')}>
              View all
            </button>
          </div>
        </div>
        <div className="max-h-[440px] overflow-auto">
          {active.length === 0 ? (
            <div className="px-4 py-8 text-center text-xs text-muted-foreground">
              You're all caught up. No active alerts.
            </div>
          ) : (
            active.slice(0, 10).map(a => <AlertItem key={a.id} a={a} />)
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function AlertItem({ a }: { a: MergedAlert }) {
  const Icon = ICONS[a.kind] ?? AlertTriangle;
  const navigate = useNavigate();
  const update = useUpdateAlertState();
  const { agencyId } = useAgency();

  const click = () => {
    if (a.isUnread) {
      update.mutate({
        alertKey: a.id, alertKind: a.kind,
        brandId: a.brandId ?? null, agencyId: agencyId ?? null,
        status: 'read',
      });
    }
    if (!a.href) return;
    if (a.href.startsWith('http')) window.open(a.href, '_blank');
    else navigate(a.href);
  };
  return (
    <div className="w-full px-3 py-2.5 hover:bg-muted/40 border-b border-border last:border-b-0 transition-colors">
      <div className="flex items-start gap-2.5">
        <span className={`mt-0.5 h-7 w-7 rounded-lg flex items-center justify-center shrink-0 ${SEV_CLS[a.severity]}`}>
          <Icon className="h-3.5 w-3.5" />
        </span>
        <button onClick={click} className="min-w-0 flex-1 text-left">
          <div className="flex items-center gap-1.5">
            {a.isUnread && <span className="h-1.5 w-1.5 rounded-full bg-primary shrink-0" />}
            <p className="text-xs font-semibold text-foreground truncate">{a.title}</p>
          </div>
          <p className="text-[11px] text-muted-foreground line-clamp-2">{a.body}</p>
          <p className="text-[10px] text-muted-foreground/70 mt-0.5">
            {a.brandName ? `${a.brandName} · ` : ''}{formatDistanceToNow(new Date(a.ts), { addSuffix: true })}
          </p>
        </button>
        <AlertActions alert={a} compact />
      </div>
    </div>
  );
}
